import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Edit, MapPin, Trash2 } from 'lucide-react';
import { Button } from '@ooh/ui/components/button';
import { InfoHint } from '@ooh/ui/components/info-hint';
import { formatDisplayDateTime } from '@ooh/ui/lib/date-format';
import { Link } from '../../plugs/link';
import { useHasPermission } from '../../plugs/current-user';
import {
  buildAvailabilityLegendItems,
  buildInventoryAvailabilityGrid,
  type InventoryOccupancyByRowId,
} from '../inventory/components/inventory-availability-shared';
import { buildPeriods } from '../inventory/lib/build-periods';
import type { Granularity } from '../inventory/components/availability-granularity-select';
import type { AvailabilityGridProps } from '../../availability/components/availability-grid';
import type {
  AvailabilityGridRow,
  AvailabilityPeriod,
  CellSelection,
  CellState,
  EntriesGridData,
  MultiCellSelection,
} from '../../availability/lib/availability-types';
import type { DetailViewUnit } from './detail-view-unit';
import { DetailSidebar } from './detail-sidebar';
import { TechSpecCard } from './tech-spec-card';
import { DeleteMediaUnitDialog } from './delete-media-unit-dialog';
import { DetailAvailabilityCard } from './detail-availability-card';
import { DetailLocationCard } from './detail-location-card';
import { DetailGallerySection } from './detail-gallery-section';
import { DetailTagsCard } from './detail-tags-card';

// eslint-disable-next-line complexity -- per-section presence gates and optional props add branching; the sub-cards already live in their own components
export function MediaUnitDetailView({
  unit,
  canViewCosts,
  occupancyByRowId = {},
  entriesGrid,
  selection = null,
  windowStartDate,
  onCellClick,
  onClosePopover,
  onPrevWindow,
  onNextWindow,
  onCellMouseDown,
  onCellMouseEnter,
  onCellMouseUp,
  disableInternalPopover = false,
  multiSelect = false,
  multiSelection = null,
  onMultiSelectionChange,
  renderCellContent,
  headerActions,
  showAvailability = false,
  backLink,
  catalogSearch,
  onDeleted = () => {},
  onPageTitleChange,
  clientView = false,
  granularity = 'month',
  onGranularityChange = () => {},
}: {
  unit: DetailViewUnit;
  canViewCosts: boolean;
  occupancyByRowId?: InventoryOccupancyByRowId;
  /** Per-cell entries for window-sourced dashed-border rendering (fromAvailabilityWindow). */
  entriesGrid?: EntriesGridData;
  selection?: CellSelection | null;
  /** Start date of the current availability window. When provided, period columns shift with prev/next. */
  windowStartDate?: Date;
  onCellClick?: AvailabilityGridProps['onCellClick'];
  onClosePopover?: () => void;
  onPrevWindow?: () => void;
  onNextWindow?: () => void;
  onCellMouseDown?: AvailabilityGridProps['onCellMouseDown'];
  onCellMouseEnter?: AvailabilityGridProps['onCellMouseEnter'];
  onCellMouseUp?: AvailabilityGridProps['onCellMouseUp'];
  /** The caller renders its own popover for occupied cells instead of the grid's. */
  disableInternalPopover?: boolean;
  multiSelect?: boolean;
  multiSelection?: MultiCellSelection | null;
  onMultiSelectionChange?: (selection: MultiCellSelection | null) => void;
  renderCellContent?: (
    row: AvailabilityGridRow,
    period: AvailabilityPeriod,
    cellState: CellState,
    colWidth: number,
  ) => React.ReactNode;
  /** Route-level actions (e.g. "Dodaj do oferty") rendered next to edit and delete. */
  headerActions?: React.ReactNode;
  /** The client payload has no availability, so only the internal route turns this on. */
  showAvailability?: boolean;
  /**
   * Back-link slot. Defaults to a plain link to the catalog; the apps supply
   * their own link when the way back carries state (catalog filters, an offer view).
   */
  backLink?: React.ReactNode;
  /**
   * Opaque, serialized catalog filter/sort state forwarded from the `/media-units`
   * route via the row link. Forwarded to the edit page and to the bundle partner
   * links so their own back-links keep the exact catalog view.
   */
  catalogSearch?: string;
  /** Navigation after the unit was deleted; the app decides where the user lands. */
  onDeleted?: () => void;
  /** Reports the page title (the unit's display number) to the app shell. */
  onPageTitleChange?: (title: string) => void;
  /**
   * Client-facing audience: hides both Google Maps affordances, the internal client-visible tag
   * marker and the edit/delete actions, independent of the permission plug (which fails open)
   * and of which fields the payload carries.
   */
  clientView?: boolean;
  /** Owned by the caller so the calendar and the caller's selection logic share one granularity. */
  granularity?: Granularity;
  onGranularityChange?: (granularity: Granularity) => void;
}) {
  const { t } = useTranslation('inventory');
  const { t: tCommon } = useTranslation('common');
  const canEdit = useHasPermission('media_unit.edit');
  const canManageAttachments = useHasPermission('attachment.manage');
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = React.useState(false);

  const unitRowId = String(unit.id);
  const availabilityPeriods = React.useMemo(
    () => buildPeriods(granularity, 12, windowStartDate),
    [granularity, windowStartDate],
  );
  const availabilityLegend = React.useMemo(() => buildAvailabilityLegendItems(tCommon), [tCommon]);
  const availabilityGrid = React.useMemo(
    () => buildInventoryAvailabilityGrid([unitRowId], availabilityPeriods, occupancyByRowId),
    [unitRowId, availabilityPeriods, occupancyByRowId],
  );

  React.useEffect(() => {
    onPageTitleChange?.(t('detail.pageTitle', { displayNumber: unit.displayNumber }));
  }, [onPageTitleChange, unit.displayNumber, t]);

  return (
    <div className="p-6 max-w-6xl mx-auto" data-testid="media-detail">
      <div className="flex items-center gap-3 mb-5">
        {backLink ?? (
          <Link
            to="/media-units"
            className="inline-flex items-center gap-1.5 text-[13px] text-muted-foreground hover:text-foreground transition-colors"
            data-testid="back-link"
          >
            <ArrowLeft className="h-4 w-4" />
            {t('detail.backLink')}
          </Link>
        )}
      </div>

      <div className="flex items-start justify-between mb-5">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <h2 className="text-2xl font-bold tracking-tight">
              {unit.city}
              {unit.address ? `, ${unit.address}` : ''}
            </h2>
          </div>
          <p className="text-xs font-mono text-muted-foreground" data-testid="display-number">
            {unit.displayNumber}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {headerActions}
          {!clientView && unit.grwId != null && canEdit ? (
            <>
              <Button
                variant="ghost"
                size="sm"
                data-testid="delete-button"
                className="text-destructive hover:text-destructive hover:bg-destructive/10"
                onClick={() => setIsDeleteDialogOpen(true)}
              >
                <Trash2 className="h-4 w-4 mr-1.5" />
                {t('detail.deleteButton')}
              </Button>
              <Button asChild variant="default" size="sm" data-testid="edit-button">
                <Link
                  to={`/media-units/${unit.id}/edit`}
                  search={catalogSearch != null ? { catalogSearch } : undefined}
                >
                  <Edit className="h-4 w-4" />
                  {t('detail.editButton')}
                </Link>
              </Button>
            </>
          ) : null}
        </div>
        {unit.grwId != null ? (
          <DeleteMediaUnitDialog
            isOpen={isDeleteDialogOpen}
            mediaUnitId={unit.id}
            grwId={unit.grwId}
            onClose={() => setIsDeleteDialogOpen(false)}
            onDeleted={onDeleted}
          />
        ) : null}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* LEFT COLUMN: workshop order: gallery → map → availability → tech-spec → tags */}
        <div className="lg:col-span-2 space-y-6">
          <DetailGallerySection unit={unit} />

          <DetailLocationCard
            title={t('detail.sections.location')}
            latitude={unit.latitude}
            longitude={unit.longitude}
            pinId={String(unit.id)}
            descriptionLabel={t('detail.fields.locationDescription')}
            description={unit.locationDescription}
            footerAction={
              !clientView && unit.latitude != null && unit.longitude != null ? (
                <Button asChild variant="outline" size="sm" fullWidth>
                  <a
                    href={`https://www.google.com/maps?q=${unit.latitude},${unit.longitude}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <MapPin className="h-3.5 w-3.5" />
                    {t('detail.openInGoogleMaps')}
                  </a>
                </Button>
              ) : undefined
            }
          />

          {showAvailability ? (
            <div data-testid="availability-card">
              <DetailAvailabilityCard
                title={t('detail.availabilityCalendar')}
                titleExtra={
                  unit.availabilityLastUpdatedAt != null ? (
                    <InfoHint label={t('detail.availabilityLastUpdatedHintLabel')}>
                      {t('detail.availabilityLastUpdatedHint', {
                        date: formatDisplayDateTime(unit.availabilityLastUpdatedAt),
                      })}
                    </InfoHint>
                  ) : undefined
                }
                granularity={granularity}
                onGranularityChange={onGranularityChange}
                granularityTestId="detail-granularity-select"
                rows={[
                  {
                    id: unitRowId,
                    label: unit.supplierNumber ?? unit.grwId ?? unit.displayNumber,
                    city: unit.city,
                  },
                ]}
                periods={availabilityPeriods}
                grid={availabilityGrid}
                entriesGrid={entriesGrid}
                selection={selection}
                onCellClick={onCellClick}
                onClosePopover={onClosePopover}
                onPrevWindow={onPrevWindow}
                onNextWindow={onNextWindow}
                onCellMouseDown={onCellMouseDown}
                onCellMouseEnter={onCellMouseEnter}
                onCellMouseUp={onCellMouseUp}
                disableInternalPopover={disableInternalPopover}
                multiSelect={multiSelect}
                multiSelection={multiSelection}
                onMultiSelectionChange={onMultiSelectionChange}
                renderCellContent={renderCellContent}
                popoverLabels={{
                  reserveMulti: () => '',
                  reserveSingle: (period, unitLabel) =>
                    t('availability.reserveSingle', { period, unitLabel }),
                  releaseLabel: '',
                  occupiedByOther: t('availability.occupiedByOther'),
                  autoSnapWarning: () => '',
                  addOptionBtn: '',
                  reserveBtn: '',
                  releaseBtn: '',
                  cancelBtn: t('availability.cancelBtn'),
                  closeBtn: t('availability.closeBtn'),
                  ttlCountdownLabel: '',
                }}
                soldAriaLabel={tCommon('status.sold')}
                optionAriaLabel={tCommon('status.option')}
                reservedAriaLabel={tCommon('status.reserved')}
                legendItems={availabilityLegend}
              />
            </div>
          ) : null}

          {'attachments' in unit && unit.attachments != null ? (
            <TechSpecCard
              mediaUnitId={unit.id}
              attachments={unit.attachments}
              canManage={canManageAttachments}
            />
          ) : null}

          <DetailTagsCard tags={unit.tags ?? []} clientView={clientView} />
        </div>

        <DetailSidebar
          unit={unit}
          canViewCosts={canViewCosts}
          catalogSearch={catalogSearch}
          clientView={clientView}
        />
      </div>
    </div>
  );
}
